import { Section } from '@/components/containers/section';
import { useTranslation } from '@/contexts/i18n-context';
import { useEncounter } from '@/contexts/encounter-context';
import { DataRow, ExpandableInfo } from '@/components/encounter/review/expandable-info';
import { formatDate } from '@/util/formats';
import { Banner } from '@/components/banner';
import clsx from 'clsx';
import SkinColorStyles from '@/pages/visit-flow/health-and-lifestyle/health-and-lifestyle-page.module.css';
import styles from './medical-info-section.module.css';
import reviewStyles from '@/styles/review-page.module.css';
import { useGetEncountersMedicalIntake } from '@/api/get-medical-intake';
import { useGetEncountersMedicalHistory } from '@/api/get-medical-history';
import { Loader } from '../loaders/loader';

const translationBase = 'visit.review';
const translationBaseHealthAndLifestyle = 'visit.healthAndLifestyle';
const translationBaseMedicalHistory = 'visit.medicalHistory';
const translationBaseSkinTypeAndRiskFactors =
  'visit.healthAndLifestyle.skinTypeAndRiskFactorsSection';

interface MedicalInfoSectionProps {
  showEditButton?: boolean;
  showNotALiveVisitDisclaimer: boolean;
}

export function MedicalInfoSection({
  showEditButton = true,
  showNotALiveVisitDisclaimer,
}: MedicalInfoSectionProps) {
  const { t } = useTranslation();
  const { encounter } = useEncounter();
  const { data: medicalIntake, isFetching: isFetchingMedicalIntake } =
    useGetEncountersMedicalIntake(encounter.id);
  const { data: medicalHistory, isFetching: isFetchingMedicalHistory } =
    useGetEncountersMedicalHistory(encounter.id);

  return (
    <Section
      className={reviewStyles.section}
      title={t(`${translationBase}.medicalInfo.title`)}
      dataTestId="review-section-clinical-information"
    >
      <ExpandableInfo
        title={t(`${translationBase}.medicalInfo.healthAndLifestyle`)}
        {...(showEditButton && {
          editProps: {
            editTo: `/visit/${encounter.id}/health-and-lifestyle`,
            editModeParam: 'medicalIntake',
          },
        })}
      >
        {isFetchingMedicalIntake && <Loader type="small" />}
        {medicalIntake?.medicalIntake ? (
          <>
            {medicalIntake.medicalIntake.skinType && (
              <DataRow title={t(`${translationBase}.medicalInfo.skinType`)}>
                <p className={reviewStyles.chip}>
                  {t(
                    `${translationBaseSkinTypeAndRiskFactors}.skinType.options.${medicalIntake.medicalIntake.skinType}`,
                  )}
                </p>
              </DataRow>
            )}
            {medicalIntake.medicalIntake.skinTone && (
              <DataRow title={t(`${translationBase}.medicalInfo.skinTone`)}>
                <p
                  className={clsx(
                    reviewStyles.chip,
                    SkinColorStyles[medicalIntake.medicalIntake.skinTone],
                    styles.skinTone,
                  )}
                >
                  {t(
                    `${translationBaseSkinTypeAndRiskFactors}.skinTone.options.${medicalIntake.medicalIntake.skinTone}`,
                  )}
                </p>
              </DataRow>
            )}
            {medicalIntake.medicalIntake.ethnicity && (
              <DataRow title={t(`${translationBase}.medicalInfo.ethnicity`)}>
                <p className={reviewStyles.chip}>
                  {t(
                    `${translationBaseSkinTypeAndRiskFactors}.ethnicities.${medicalIntake.medicalIntake.ethnicity}`,
                  )}
                </p>
              </DataRow>
            )}
            <DataRow title={t(`${translationBase}.medicalInfo.height`)}>
              {medicalIntake.medicalIntake.heightFeet}&apos;
              {medicalIntake.medicalIntake.heightInches}&quot;
            </DataRow>
            <DataRow title={t(`${translationBase}.medicalInfo.weight`)}>
              {medicalIntake.medicalIntake.weight} lbs
            </DataRow>
            {medicalIntake.medicalIntake.employmentStatus && (
              <DataRow title={t(`${translationBase}.medicalInfo.employmentStatus`)}>
                {medicalIntake.medicalIntake.employmentStatus}
              </DataRow>
            )}
            {medicalIntake.medicalIntake.alcoholConsumption && (
              <DataRow title={t(`${translationBase}.medicalInfo.alcoholConsumption`)}>
                <p className={reviewStyles.chip}>
                  {t(
                    `${translationBaseSkinTypeAndRiskFactors}.alcoholConsumption.options.${medicalIntake.medicalIntake.alcoholConsumption}`,
                  )}
                </p>
              </DataRow>
            )}
            {medicalIntake.medicalIntake.tobaccoConsumption && (
              <DataRow title={t(`${translationBase}.medicalInfo.tobaccoConsumption`)}>
                <p className={reviewStyles.chip}>
                  {t(
                    `${translationBaseSkinTypeAndRiskFactors}.tobaccoConsumption.options.${medicalIntake.medicalIntake.tobaccoConsumption}`,
                  )}
                </p>
              </DataRow>
            )}
            {medicalIntake.medicalIntake.timeOutdoorsFrequency && (
              <DataRow title={t(`${translationBase}.medicalInfo.timeOutdoorsFrequency`)}>
                <p className={reviewStyles.chip}>
                  {t(
                    `${translationBaseSkinTypeAndRiskFactors}.frequencies.${medicalIntake.medicalIntake.timeOutdoorsFrequency}`,
                  )}
                </p>
              </DataRow>
            )}
            {medicalIntake.medicalIntake.sunbathFrequency && (
              <DataRow title={t(`${translationBase}.medicalInfo.sunbathFrequency`)}>
                <p className={reviewStyles.chip}>
                  {t(
                    `${translationBaseSkinTypeAndRiskFactors}.frequencies.${medicalIntake.medicalIntake.sunbathFrequency}`,
                  )}
                </p>
              </DataRow>
            )}
            {medicalIntake?.medicalIntake?.lifeFactors && (
              <DataRow title={t(`${translationBase}.medicalInfo.lifeStyle`)}>
                {medicalIntake?.medicalIntake?.lifeFactors
                  ?.map((lifeFactor) =>
                    t(`${translationBaseSkinTypeAndRiskFactors}.lifestyle.${lifeFactor}`),
                  )
                  .join(', ')}
              </DataRow>
            )}
            {medicalIntake.medicalIntake.occupation && (
              <DataRow title={t(`${translationBase}.medicalInfo.occupation`)}>
                {medicalIntake.medicalIntake.occupation}
              </DataRow>
            )}
            <DataRow title={t(`${translationBase}.medicalInfo.visitGender`)}>
              {medicalIntake.medicalIntake.visitGenderOther ? (
                medicalIntake.medicalIntake.visitGenderOther
              ) : (
                <p className={reviewStyles.chip}>
                  {t(`common.gender.${medicalIntake.medicalIntake.visitGender}`)}
                </p>
              )}
            </DataRow>
            <DataRow title={t(`${translationBase}.medicalInfo.hasUterus`)}>
              <p className={reviewStyles.chip}>
                {medicalIntake.medicalIntake.uterus ? t('common.yes') : t('common.no')}
              </p>
            </DataRow>
            {medicalIntake.medicalIntake.femaleQuestion && (
              <DataRow title={t(`${translationBase}.medicalInfo.reproductiveDetails`)}>
                <p className={reviewStyles.chip}>
                  {t(
                    `${translationBaseHealthAndLifestyle}.reproductiveHealthSection.femaleQuestion.options.${medicalIntake.medicalIntake.femaleQuestion}`,
                  )}
                </p>
              </DataRow>
            )}
            {medicalIntake.medicalIntake.dueDate && (
              <DataRow title={t(`${translationBase}.medicalInfo.dueDate`)}>
                {formatDate(medicalIntake.medicalIntake.dueDate)}
              </DataRow>
            )}
          </>
        ) : (
          <p>{t(`${translationBase}.medicalInfo.error`)}</p>
        )}
      </ExpandableInfo>

      <div className={reviewStyles.divider} />

      <ExpandableInfo
        title={t(`${translationBase}.medicalInfo.history`)}
        {...(showEditButton && {
          editProps: {
            editTo: `/visit/${encounter.id}/medical-history`,
            editModeParam: 'medicalHistory',
          },
        })}
      >
        {isFetchingMedicalHistory && <Loader type="small" />}
        {medicalHistory ? (
          <>
            <DataRow title={t(`${translationBase}.medicalInfo.medications`)}>
              {medicalHistory.medications.map((medication) => medication.name).join(', ') ||
                t(`${translationBase}.none`)}
            </DataRow>
            <DataRow title={t(`${translationBase}.medicalInfo.surgeries`)}>
              {medicalHistory.surgeries.join(', ') || t(`${translationBase}.none`)}
            </DataRow>
            <DataRow title={t(`${translationBase}.medicalInfo.allergies`)}>
              {medicalHistory.allergies.map((allergy) => allergy.name).join(', ') ||
                t(`${translationBase}.none`)}
            </DataRow>
            <DataRow title={t(`${translationBase}.medicalInfo.conditions`)}>
              {medicalHistory.familyDiseases.join(', ') || t(`${translationBase}.none`)}
            </DataRow>
            <DataRow title={t(`${translationBase}.medicalInfo.illnesses`)}>
              {medicalHistory.illnesses
                .map(
                  (illness) =>
                    `${illness.name}${illness.type ? ` (${t(`${translationBaseMedicalHistory}.illnessesForm.illnessType.${illness.type}`)})` : ''}`,
                )
                .join(', ') || t(`${translationBase}.none`)}
            </DataRow>
          </>
        ) : (
          <p>{t(`${translationBase}.medicalInfo.error`)}</p>
        )}
      </ExpandableInfo>

      {showNotALiveVisitDisclaimer && (
        <Banner type="warning">
          <span className="body-3">
            {encounter.isVideoRequired
              ? t(`${translationBase}.medicalInfo.notLiveVisitDescriptionWithCall`)
              : t(`${translationBase}.medicalInfo.notLiveVisitDescription`)}
          </span>
        </Banner>
      )}
    </Section>
  );
}
